import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Calendar } from './ui/calendar';
import { CheckCircle, X, Clock } from 'lucide-react';

export const BookingModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    timeSlot: ''
  });
  const [bookingConfirmed, setBookingConfirmed] = useState(false);

  const timeSlots = [
    '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM',
    '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM'
  ];

  const services = [
    'BPS Denture',
    'Dental Implant',
    'Root Canal',
    'Dental Laminates',
    'Smile Design',
    'General Checkup',
    'Tooth Polishing',
    'Other'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare data for API
    const appointmentData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      date: date.toISOString().split('T')[0], // Format: YYYY-MM-DD
      timeSlot: formData.timeSlot,
      service: formData.service
    };
    
    try {
      // Get API URL from environment or use relative path
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${API_URL}/api/appointments.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setBookingConfirmed(true);
        setTimeout(() => {
          setBookingConfirmed(false);
          setStep(1);
          setDate(null);
          setFormData({ name: '', email: '', phone: '', service: '', timeSlot: '' });
          onClose();
        }, 3000);
      } else {
        alert('Error: ' + (result.error || 'Failed to book appointment'));
      }
    } catch (error) {
      console.error('Booking error:', error);
      alert('Failed to book appointment. Please try again or call us at 099757 27098');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetAndClose = () => {
    setStep(1);
    setDate(null);
    setFormData({ name: '', email: '', phone: '', service: '', timeSlot: '' });
    setBookingConfirmed(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        <button
          onClick={resetAndClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
          data-testid="close-booking-modal"
        >
          <X className="h-4 w-4" />
        </button>

        {bookingConfirmed ? (
          <div className="py-12 text-center" data-testid="booking-confirmation">
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
            <h3 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              Appointment Booked!
            </h3>
            <p className="text-lg text-slate-600 mb-2">
              Your appointment has been scheduled for
            </p>
            <p className="text-xl font-bold text-primary mb-4">
              {date?.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at {formData.timeSlot}
            </p>
            <p className="text-slate-600">
              We'll send you a confirmation via email and SMS shortly.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-3xl">Book Your Appointment</DialogTitle>
              <DialogDescription>
                Choose your preferred date, time, and service
              </DialogDescription>
            </DialogHeader>

            <div className="flex gap-2 mb-6 border-b">
              <button
                onClick={() => setStep(1)}
                className={`px-6 py-3 font-semibold transition-all ${
                  step === 1
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-slate-400'
                }`}
                data-testid="step-1-button"
              >
                1. Select Date
              </button>
              <button
                onClick={() => date && setStep(2)}
                disabled={!date}
                className={`px-6 py-3 font-semibold transition-all ${
                  step === 2
                    ? 'text-primary border-b-2 border-primary'
                    : date ? 'text-slate-400' : 'text-slate-300 cursor-not-allowed'
                }`}
                data-testid="step-2-button"
              >
                2. Select Time
              </button>
              <button
                onClick={() => date && formData.timeSlot && setStep(3)}
                disabled={!date || !formData.timeSlot}
                className={`px-6 py-3 font-semibold transition-all ${
                  step === 3
                    ? 'text-primary border-b-2 border-primary'
                    : (date && formData.timeSlot) ? 'text-slate-400' : 'text-slate-300 cursor-not-allowed'
                }`}
                data-testid="step-3-button"
              >
                3. Your Details
              </button>
            </div>

            {step === 1 && (
              <div className="flex justify-center py-4" data-testid="calendar-step">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(selectedDate) => {
                    setDate(selectedDate);
                    if (selectedDate) {
                      setTimeout(() => setStep(2), 300); // Auto progress to step 2
                    }
                  }}
                  disabled={(date) => date < new Date() || date.getDay() === 0}
                  className="rounded-md border"
                />
              </div>
            )}

            {step === 2 && (
              <div data-testid="timeslot-step">
                <h3 className="font-heading font-bold text-xl mb-4">
                  Select Time Slot
                </h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => {
                        setFormData({ ...formData, timeSlot: slot });
                        setStep(3);
                      }}
                      data-testid={`timeslot-${slot.replace(/\s+/g, '-')}`}
                      className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                        formData.timeSlot === slot
                          ? 'bg-primary text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      <Clock className="w-4 h-4 inline mr-2" />
                      {slot}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="details-step">
                <div className="bg-blue-50 p-4 rounded-lg mb-6">
                  <p className="text-sm text-slate-700">
                    <strong>Date:</strong> {date?.toLocaleDateString('en-IN')} <br />
                    <strong>Time:</strong> {formData.timeSlot}
                  </p>
                </div>

                <div>
                  <label htmlFor="booking-name" className="block text-sm font-semibold text-slate-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="booking-name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    data-testid="booking-input-name"
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                    placeholder="Your full name"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="booking-email" className="block text-sm font-semibold text-slate-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="booking-email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="booking-input-email"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="booking-phone" className="block text-sm font-semibold text-slate-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="booking-phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="booking-input-phone"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                      placeholder="Your phone number"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="booking-service" className="block text-sm font-semibold text-slate-700 mb-2">
                    Service *
                  </label>
                  <select
                    id="booking-service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={handleChange}
                    data-testid="booking-select-service"
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  data-testid="confirm-booking-button"
                  className="w-full px-8 py-4 rounded-full bg-gradient-to-r from-primary to-secondary text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  Confirm Appointment
                </button>
              </form>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
